// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import {
  findLabel,
  MOCK_AVAILABLE_DATES,
  MOCK_AVAILABLE_TIMES,
  MOCK_PRICE,
  MOCK_PROVIDER,
  mockAppointmentTypeOptions,
  paymentMethodOptions
} from "#/features/telemedicine/booking/mockBookingData"
import { BackIcon, Box, Divider, IconButton, Link, Typography } from "@carrotfertility/carotene-core"
import { FormButton, FormCheckbox, FormTextField, useWatch } from "@carrotfertility/carotene-core-x"
import React from "react"

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>{label}</Typography>
      <Typography>{value}</Typography>
    </Box>
  )
}

type PaymentStepProps = {
  onBack: () => void
}

// Reads the appointment summary via useWatch on the shared form (same instance
// BookingDetailsStep filled in) instead of a `booking` prop — nothing needs to be
// handed down/captured across steps anymore since it's all one form.
export function PaymentStep({ onBack }: PaymentStepProps) {
  const [appointmentType, appointmentDate, appointmentTime, paymentMethod] = useWatch({
    name: ["appointment.type", "appointment.date", "appointment.time", "payment.method"]
  })

  return (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.lg })}>
      <IconButton aria-label="Back" size="small" color="secondary" onClick={onBack} sx={{ inlineSize: "fit-content" }}>
        <BackIcon />
      </IconButton>
      <Typography variant="h1">Confirm and secure your appointment</Typography>

      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.tokens.spacing.sm,
          border: `${theme.tokens.borderWidth.sm} solid ${theme.palette.border.light}`,
          borderRadius: theme.tokens.borderRadius.md,
          padding: theme.spacing(theme.tokens.spacing.lg)
        })}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: "bold" }}>Appointment details</Typography>
          <Link component="button" type="button" onClick={onBack}>
            Update
          </Link>
        </Box>
        <DetailRow label="Visit" value={`Carrot Clinic · ${findLabel(mockAppointmentTypeOptions, appointmentType)}`} />
        <DetailRow label="Provider" value={MOCK_PROVIDER} />
        <DetailRow
          label="Date & time"
          value={`${findLabel(MOCK_AVAILABLE_DATES, appointmentDate)} · ${findLabel(
            MOCK_AVAILABLE_TIMES,
            appointmentTime
          )}`}
        />
        <DetailRow label="Payment" value={`${findLabel(paymentMethodOptions, paymentMethod)} (${MOCK_PRICE})`} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: "bold" }}>Authorized amount</Typography>
        <Typography variant="h3">{MOCK_PRICE}</Typography>
      </Box>
      <Divider />
      <Typography>
        Your card will be <b>authorized now but charged only after your appointment is complete</b>.
      </Typography>

      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.md })}>
        <Typography sx={{ fontWeight: "bold" }}>Payment details</Typography>
        <FormTextField name="card.name" label="Name on card" registerOptions={{ required: true }} />
        <FormTextField name="card.number" label="Card number" registerOptions={{ required: true }} />
        <Box sx={(theme) => ({ display: "flex", gap: theme.tokens.spacing.md })}>
          <FormTextField name="card.expiry" label="Expiry" placeholder="MM/YY" registerOptions={{ required: true }} />
          <FormTextField name="card.cvc" label="CVC" registerOptions={{ required: true }} />
          <FormTextField name="card.zip" label="ZIP" registerOptions={{ required: true }} />
        </Box>
        <FormCheckbox name="card.saveCard" label="Save this card for future consultations" />
        <Link component="button" type="button">
          Review our cancelation policy.
        </Link>
        <FormButton type="submit" color="secondary">
          Authorize &amp; confirm booking
        </FormButton>
      </Box>
    </Box>
  )
}
